import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import TimePicker from '@components/TimePicker';
import DeleteModal from '@components/DeleteModal';
import Icon from '@material-ui/core/Icon';
import Alert from '@material-ui/lab/Alert';
import { apiClient } from '@services/api';
import { useEffect, useState, useRef } from 'react';
import NumberFormat from 'react-number-format';
import { Link, useHistory, useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import RequestTable from './partials/RequestTable';
import PaymentsDetail from './partials/PaymentsDetail';
import SendVoucherModal from './partials/SendVoucherModal';

/* ── paleta ────────────────────────────────── */
const C = {
  bg:        '#0f1e35',
  card:      '#172B4D',
  hover:     '#1e3a5a',
  accent:    '#62DEC2',
  textSec:   '#8ba5c4',
  textMain:  '#e8f0fb',
  btnDef:    '#2a4f72',
};

/* ── badge do status do cliente ─────────────── */
const CustomerStatusBadge = ({ presales }) => {
  const today = new Date();
  const expiresAt = presales?.expires_at ? new Date(presales.expires_at) : null;
  const isExpired = expiresAt && expiresAt < today;

  if (presales?.customer_status === 'approved') {
    return (
      <span style={{
        background: '#166534',
        color: '#bbf7d0',
        border: '1px solid #22c55e',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>check_circle</Icon>
        Cliente aprovou ✓
      </span>
    );
  }

  if (presales?.customer_status === 'refused') {
    return (
      <span style={{
        background: '#7f1d1d',
        color: '#fecaca',
        border: '1px solid #ef4444',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>cancel</Icon>
        Cliente recusou
        {presales?.customer_refusal_reason && (
          <span style={{ fontWeight: 400, fontSize: 12 }}>
            — {presales.customer_refusal_reason}
          </span>
        )}
      </span>
    );
  }

  if (isExpired) {
    return (
      <span style={{
        background: '#78350f',
        color: '#fde68a',
        border: '1px solid #f59e0b',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>schedule</Icon>
        Expirado
      </span>
    );
  }

  return null;
};

/* ── thread de comentários ──────────────────── */
const CommentsThread = ({ presalesId }) => {
  const [comments, setComments]   = useState([]);
  const [message, setMessage]     = useState('');
  const [sending, setSending]     = useState(false);
  const [loadError, setLoadError] = useState(false);
  const bottomRef = useRef(null);

  const loadComments = () => {
    apiClient.httpInstance
      .get(`/presales/${presalesId}/comments`)
      .then(res => {
        if (res.status === 200) setComments(res.data);
      })
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    loadComments();
  }, [presalesId]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const handleSend = () => {
    if (!message.trim()) return;
    setSending(true);
    apiClient.httpInstance
      .post(`/presales/${presalesId}/comments`, { message: message.trim() })
      .then(res => {
        if (res.status === 200 || res.status === 201) {
          setMessage('');
          loadComments();
        }
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ marginTop: 24, marginBottom: 24 }}>
      <div style={{ color: C.accent, fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
        Mensagens com o cliente
      </div>
      <div style={{
        background: C.card,
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxHeight: 420,
        overflowY: 'auto',
      }}>
        {loadError && (
          <div style={{ color: C.textSec, fontSize: 13, textAlign: 'center' }}>
            Não foi possível carregar os comentários.
          </div>
        )}
        {!loadError && comments.length === 0 && (
          <div style={{ color: C.textSec, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            Nenhuma mensagem ainda.
          </div>
        )}
        {comments.map((c) => {
          const isConsultor = c.author_type === 'consultant' || c.author_type === 'user';
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isConsultor ? 'flex-end' : 'flex-start',
              }}
            >
              <div style={{ color: C.textSec, fontSize: 11, marginBottom: 3 }}>
                {c.author_name}
              </div>
              <div style={{
                background: isConsultor ? C.btnDef : C.hover,
                color: C.textMain,
                borderRadius: 12,
                padding: '8px 12px',
                maxWidth: '70%',
                wordBreak: 'break-word',
                fontSize: 14,
              }}>
                {c.message}
              </div>
              <div style={{ color: C.textSec, fontSize: 10, marginTop: 3 }}>
                {formatDate(c.created_at)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input para envio */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-end' }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Digite uma mensagem..."
          rows={2}
          style={{
            flex: 1,
            background: C.card,
            border: `1px solid ${C.hover}`,
            borderRadius: 8,
            color: C.textMain,
            padding: '10px 12px',
            fontSize: 14,
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        <button
          onClick={handleSend}
          disabled={sending || !message.trim()}
          style={{
            background: sending || !message.trim() ? '#1e3a5a' : C.btnDef,
            color: C.textMain,
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            cursor: sending || !message.trim() ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background 0.2s',
          }}
        >
          <Icon style={{ fontSize: 18 }}>send</Icon>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};

/* ── componente principal ───────────────────── */
const Show = (props) => {
  document.title = `Detalhes do orçamento - ${process.env.REACT_APP_NAME}`
  const locationState = useLocation().state;
  const userPermission = props?.user?.data?.permission || '';
  const isManager = ['administrador', 'gerente'].includes(userPermission.toLowerCase());
  // approve = true se veio da tela de aprovações OU se é gerente/admin e há pedido gerencial pendente
  const approve = locationState?.approve || false;
  const { presales_id } = useParams();
  const [presales, setPreSale] = useState({});
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendVoucherModal, setSendVoucherModal] = useState(false);
  const [disableModal, setDisableModal] = useState(false)
  const [urlLink, setUrlLink] = useState(null)
  // Modal de checagem de datas antes da conversão
  const [dateCheckModal, setDateCheckModal] = useState(false)
  const [dateIssues, setDateIssues]         = useState([])
  const [fixDates, setFixDates]             = useState({}) // { budget_id: 'YYYY-MM-DD' }
  const [fixAvailDates, setFixAvailDates]   = useState({}) // { budget_id: [{ cupo_date, total_cupos }] }
  const [converting, setConverting]         = useState(false)
  // Modal de confirmação de dados de transfer antes de converter
  const [transferInfoModal, setTransferInfoModal] = useState(false)
  const [transferIssues, setTransferIssues]       = useState([]) // budgets de transfer com dados incompletos
  const [transferFix, setTransferFix]             = useState({}) // { budget_id: { date, flight_code, flight_time } }
  const [sendEmailModal, setSendEmailModal] = useState(false)
  const [sendEmailAddress, setSendEmailAddress] = useState('')
  const [sendEmailLoading, setSendEmailLoading] = useState(false)
  const [sendEmailSuccess, setSendEmailSuccess] = useState(false)
  // Aprovação gerencial — consultor solicita
  const [approvalForm, setApprovalForm] = useState({ type: 'discount_pct', value: '', justify: '' });
  const [approvalLoading, setApprovalLoading] = useState(false);
  // Aprovação gerencial — gerente responde
  const [managerForm, setManagerForm] = useState({ value: '', note: '' });
  const [managerLoading, setManagerLoading] = useState(false);
  // Pagamento externo (fora do Asaas) — só admin
  const [extPayModal, setExtPayModal]     = useState(false);
  const [extPayForm, setExtPayForm]       = useState({ method: 'pix', partial_pct: 100 });
  const [extPayFile, setExtPayFile]       = useState(null);
  const [extPayLoading, setExtPayLoading] = useState(false);
  const history = useHistory();

  const submitExternalPayment = () => {
    if (!extPayFile) { setError(true); setErrorMessage('Anexe o comprovante do pagamento.'); setTimeout(() => setError(false), 4000); return; }
    setExtPayLoading(true);
    const fd = new FormData();
    fd.append('payment_method', extPayForm.method);
    fd.append('partial_pct', String(extPayForm.partial_pct));
    fd.append('proof', extPayFile);
    apiClient.httpInstance.post(`/presales/${presales_id}/external-payment`, fd)
      .then(() => {
        setExtPayModal(false);
        setExtPayFile(null);
        setExtPayForm({ method: 'pix', partial_pct: 100 });
        getSalesDetails(url);
      })
      .catch((e) => {
        setError(true);
        setErrorMessage(e?.response?.data?.message || 'Erro ao registrar pagamento externo.');
        setTimeout(() => setError(false), 5000);
      })
      .finally(() => setExtPayLoading(false));
  };

  const url = `/presales/${presales_id}`

  const getSalesDetails = url => {
    apiClient.httpInstance.get(url).then(response => {
      if (response.status === 200) {
        setPreSale(response.data);
        if (response.data.url_link) {
          // Normaliza: extrai só o token caso venha como URL completa
          const raw = response.data.url_link;
          setUrlLink(raw.includes('/') ? raw.split('/').pop() : raw);
        }
        setLoading(false);
      }
    })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage("Esse orçamento não existe");
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
          setLoading(true);
        }
      });
  }

  const getClientById = (id) => {
    apiClient.httpInstance.get(`customers/${id}`).then(response => {
      if (response.status === 200) {
        setUrlLink(response.data.url_link)
        setLoading(false);
      }
    })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage("Cliente não encontrado");
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const getBudgets = url => {
    apiClient.httpInstance.get(`${url}/budgets`).then(response => {
      if (response.status === 200) {
        setBudgets(response.data);
        setLoading(false)
      }
    }).catch(error => {
      if (error) {
        setError(true);
        setErrorMessage("Erro inesperado, tente novamente mais tarde")
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      }
    });
  }

  const cancelPresale = url => {
    apiClient.httpInstance.put(url)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/orcamentos`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error.response.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const renovarPresale = () => {
    apiClient.httpInstance.post(`/presales/${presales_id}/renovar`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/orcamentos/detalhe/${response.data.presale.id}`);
        }
      })
      .catch(error => {
        setError(true);
        setErrorMessage(error?.response?.data?.message || "Erro ao renovar orçamento")
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      });
  }

  const reactivatePresale = () => {
    apiClient.httpInstance.put(`/presales/${presales_id}/reactivate`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          window.location.reload();
        }
      })
      .catch(error => {
        setError(true);
        setErrorMessage(error?.response?.data?.message || "Erro ao reativar orçamento")
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      });
  }

  // Trava manual de cartão (2026-10-21): o consultor LIGA (vira pedido de liberação gerencial, cliente só Pix);
  // só administrador/gerente REMOVE. Aprovar/recusar o cartão é no ícone ! do cabeçalho.
  const toggleCardHold = (enabled) => {
    let reason = '';
    if (enabled) {
      const r = window.prompt('Travar o cartão deste orçamento? O cliente só poderá pagar no Pix até a gestão liberar.\n\nMotivo (ex.: e-mail suspeito):');
      if (r === null) return;
      reason = r;
    } else if (!window.confirm('Remover a trava de cartão deste orçamento?')) {
      return;
    }
    apiClient.httpInstance.put(`/presales/${presales_id}/card-hold`, { enabled, reason })
      .then(() => getSalesDetails(url))
      .catch(e => {
        setError(true);
        setErrorMessage(e?.response?.data?.message || 'Erro ao alterar a trava de cartão.');
        setTimeout(() => { setError(false) }, 5000);
        setTimeout(() => { setErrorMessage(null) }, 5000);
      });
  }

  const approvePresale = () => {
    apiClient.httpInstance.post(`/presales/${presales_id}/approve`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/aprovacoes`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error.response.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const disaprovePresale = () => {
    apiClient.httpInstance.post(`/presales/${presales_id}/disapprove`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/aprovacoes`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error.response.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  // Abre modal de reenvio de email com email do cliente pré-preenchido
  const openSendEmailModal = () => {
    setSendEmailAddress(presales?.email ?? '')
    setSendEmailSuccess(false)
    setSendEmailModal(true)
  }

  // Envia email do orçamento para o endereço informado
  const handleSendEmail = () => {
    setSendEmailLoading(true)
    apiClient.httpInstance
      .post(`/presales/${presales_id}/send_document`, { email: sendEmailAddress })
      .then(() => {
        setSendEmailSuccess(true)
        setSendEmailLoading(false)
        setTimeout(() => setSendEmailModal(false), 1800)
      })
      .catch(() => {
        setSendEmailLoading(false)
        alert('Erro ao enviar e-mail. Tente novamente.')
      })
  }

  // Etapa 1: checar datas antes de converter
  // Etapa 0: verificar transfers com dados incompletos antes de check-dates
  const saleConvert = (presaleId) => {
    const transferBudgets = budgets.filter(b => b.travel_type === 'transfer');

    if (transferBudgets.length > 0) {
      const initial = {};
      transferBudgets.forEach(b => {
        initial[b.id] = {
          date:        b.tour_date   || '',
          flight_code: b.cod_fly     || '',
          flight_time: b.time_fly    || '',
        };
      });
      setTransferFix(initial);
      setTransferIssues(transferBudgets);
      setTransferInfoModal(true);
      return;
    }

    checkDatesAndConvert(presaleId);
  };

  // Etapa 0b: chamado após confirmar transfers (ou direto se não havia transfers incompletos)
  const checkDatesAndConvert = (presaleId) => {
    apiClient.httpInstance.get(`/presales/${presaleId}/check-dates`)
      .then(response => {
        const { ok, issues } = response.data;
        if (ok) {
          doConvert(presaleId);
        } else {
          // Inicializa fixDates com as datas atuais dos tours com problemas
          const initial = {};
          issues.forEach(i => { initial[i.budget_id] = i.tour_date || ''; });
          setFixDates(initial);
          setDateIssues(issues);
          // Buscar datas disponíveis para cada tour com problema
          issues.forEach(issue => {
            apiClient.httpInstance.get(`reservations_dates/available?tour_id=${issue.tour_id}`)
              .then(r => {
                setFixAvailDates(prev => ({ ...prev, [issue.budget_id]: r.data || [] }));
              })
              .catch(() => {});
          });
          setDateCheckModal(true);
        }
      })
      .catch(() => {
        setError(true);
        setErrorMessage('Não foi possível verificar disponibilidade de vagas. Tente novamente antes de converter.');
        setTimeout(() => setError(false), 5000);
        setTimeout(() => setErrorMessage(null), 5000);
      });
  };

  // Etapa 2: salvar datas corrigidas e reconverter
  const saveFixedDatesAndConvert = () => {
    setConverting(true);
    const saves = dateIssues.map(issue =>
      apiClient.httpInstance.put(
        `/presales/${presales_id}/budgets/${issue.budget_id}/date`,
        { date: fixDates[issue.budget_id] }
      )
    );
    Promise.all(saves)
      .then(() => {
        setDateCheckModal(false);
        doConvert(presales_id);
      })
      .catch(() => {
        setConverting(false);
        setError(true);
        setErrorMessage('Erro ao salvar datas. Tente novamente.');
        setTimeout(() => setError(false), 4000);
      });
  };

  // Etapa 1: salvar dados de transfer e prosseguir para check-dates
  const saveTransferInfoAndContinue = () => {
    setConverting(true);
    const saves = transferIssues.map(b => {
      const fix = transferFix[b.id] || {};
      return apiClient.httpInstance.put(
        `/presales/${presales_id}/budgets/${b.id}/date`,
        {
          date:        fix.date        || null,
          flight_code: fix.flight_code || null,
          flight_time: fix.flight_time || null,
        }
      );
    });
    Promise.all(saves)
      .then(() => {
        setTransferInfoModal(false);
        setConverting(false);
        checkDatesAndConvert(presales_id);
      })
      .catch(() => {
        setConverting(false);
        setError(true);
        setErrorMessage('Erro ao salvar dados de transfer. Tente novamente.');
        setTimeout(() => setError(false), 4000);
      });
  };

  // Converte de fato
  const doConvert = (presaleId) => {
    apiClient.httpInstance.post(`/presales/${presaleId}/convert-in-sale`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          // Fix 2026-06-07: usa o pagamento FRESCO da resposta do convert (banco) e
          // só cai no estado da tela como fallback. Pela notificação o estado `presales`
          // podia estar sem o pagamento, fazendo o carrinho perguntar pagamento de um
          // orçamento já pago no Asaas.
          const payStatus = response.data?.payment_status ?? presales.payment_status;
          const payMethod = response.data?.payment_method ?? presales.payment_method;
          const payPct    = response.data?.payment_partial_pct ?? presales.payment_partial_pct;
          const presalePayment = (payStatus && payStatus !== 'pending')
            ? {
                payment_status:      payStatus,
                payment_method:      payMethod,
                payment_partial_pct: payPct,
              }
            : null;
          history.push(`/admin/carrinho`, {
            ...(presalePayment ? { presalePayment } : {}),
            customerEmail:       presales.email || null,
            partnerSellerId:     response.data.partner_seller_id   || null,
            partnerSellerType:   response.data.partner_seller_type || null,
          });
        }
      })
      .catch(error => {
        setConverting(false);
        setError(true);
        setErrorMessage(error?.response?.data?.message || "Erro inesperado, tente novamente mais tarde");
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      });
  };

  // ── Aprovação Gerencial ──────────────────────────────────────────────────

  // Consultor solicita aprovação gerencial
  const requestApproval = () => {
    if (!approvalForm.value.trim()) return;
    setApprovalLoading(true);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/request`, {
        approval_type:    approvalForm.type,
        approval_value:   approvalForm.value.trim(),
        approval_justify: approvalForm.justify.trim() || undefined,
      })
      .then(() => getSalesDetails(url))
      .catch(() => {
        setError(true);
        setErrorMessage('Erro ao enviar pedido. Tente novamente.');
        setTimeout(() => setError(false), 4000);
        setTimeout(() => setErrorMessage(null), 4000);
      })
      .finally(() => setApprovalLoading(false));
  };

  // Gerente aprova (pode alterar o valor)
  // Também chama o endpoint antigo para sincronizar status_discount_approve
  const approveManagerApproval = () => {
    if (!(managerForm.value || presales.approval_value)) return;
    setManagerLoading(true);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/approve`, {
        approval_manager_value: (managerForm.value || presales.approval_value).trim(),
        approval_manager_note:  managerForm.note.trim() || undefined,
      })
      .then(() => {
        // Sincroniza fluxo antigo (status_discount_approve → approved)
        if (approve) {
          return apiClient.httpInstance.post(`/presales/${presales_id}/approve`).catch(() => {});
        }
      })
      .then(() => {
        if (approve) { history.push('/admin/aprovacoes'); } else { getSalesDetails(url); }
      })
      .catch(() => {
        setError(true);
        setErrorMessage('Erro ao aprovar. Tente novamente.');
        setTimeout(() => setError(false), 4000);
        setTimeout(() => setErrorMessage(null), 4000);
      })
      .finally(() => setManagerLoading(false));
  };

  // Gerente recusa
  const refuseManagerApproval = () => {
    setManagerLoading(true);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/refuse`, {
        approval_manager_note: managerForm.note.trim() || undefined,
      })
      .then(() => {
        // Sincroniza fluxo antigo (status_discount_approve → refused)
        if (approve) {
          return apiClient.httpInstance.post(`/presales/${presales_id}/disapprove`).catch(() => {});
        }
      })
      .then(() => {
        if (approve) { history.push('/admin/aprovacoes'); } else { getSalesDetails(url); }
      })
      .catch(() => {
        setError(true);
        setErrorMessage('Erro ao recusar. Tente novamente.');
        setTimeout(() => setError(false), 4000);
        setTimeout(() => setErrorMessage(null), 4000);
      })
      .finally(() => setManagerLoading(false));
  };

  // Gerente remove um desconto gerencial já aprovado (2026-06-07)
  const removeManagerApproval = () => {
    if (!window.confirm('Remover o desconto gerencial deste orçamento? Ele volta às condições sem desconto e o consultor poderá solicitar nova negociação.')) return;
    setManagerLoading(true);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/remove`)
      .then(() => getSalesDetails(url))
      .catch(() => {
        setError(true);
        setErrorMessage('Erro ao remover desconto. Tente novamente.');
        setTimeout(() => setError(false), 4000);
        setTimeout(() => setErrorMessage(null), 4000);
      })
      .finally(() => setManagerLoading(false));
  };

  const bradecrumbsConfig = [
    {
      title: `Início`,
      active: true,
      route: approve ? '/admin/aprovacoes' : '/admin/orcamentos'
    },
    {
      title: `Orçamento ${presales.references || ''}`,
    }
  ]

  const status = {
    pending: {
      text: 'Aguardando aprovação',
      icon: 'access_time'
    },
    refused: {
      text: 'Negado',
      icon: 'running_with_errors'
    },
    overdue: {
      text: 'Vencido',
      icon: 'running_with_errors'
    },
    open: {
      text: 'Em aberto',
      icon: 'access_time'
    },
    approved: {
      text: 'Aprovado',
      icon: 'check_circle_outline'
    },
    sold: {
      text: 'Convertido em venda',
      icon: 'access_time'
    },
    canceled: {
      text: 'Cancelado',
      icon: 'access_time'
    },
  }

  useEffect(() => {
    getSalesDetails(url);
    getBudgets(url);
  }, [url]);

  const cardStyle = {
    background: C.card,
    borderRadius: 10,
    marginTop: 16,
    color: C.textMain,
  };

  const cardHeaderStyle = {
    padding: '16px',
    fontSize: 18,
    color: C.textMain,
    borderBottom: `1px solid ${C.hover}`,
  };

  return <>
    {!loading &&
      <>
        {error && (
          <Alert className="mb-5" variant="filled" severity="error">
            {errorMessage}
          </Alert>
        )}
        <BreadCrumbs items={bradecrumbsConfig} />

        {/* Banner PIX-exclusivo mês vigente — visível só para o consultor */}
        {budgets.some(b => [139, 58].includes(b.tour_id) && b.tour_date && b.tour_date.slice(0, 7) === new Date().toISOString().slice(0, 7)) && (
          <div style={{
            background: '#451a03', border: '1px solid #f97316', borderRadius: 10,
            padding: '12px 20px', margin: '0 28px 16px', color: '#fed7aa', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon style={{ fontSize: 18, color: '#f97316' }}>warning_amber</Icon>
            <span>
              <strong>Orçamento exclusivo Monteluz/Alyan — mês vigente.</strong>{' '}
              Somente PIX. Não é possível adicionar outros tours a este orçamento.
            </span>
          </div>
        )}

        {/* Banner trava de cartão — visível ao consultor e à gestão */}
        {presales?.card_hold && (
          <div style={{
            background: '#2e1065', border: '1px solid #a855f7', borderRadius: 10,
            padding: '12px 20px', margin: '0 28px 16px', color: '#e9d5ff', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon style={{ fontSize: 18, color: '#a855f7' }}>lock</Icon>
            <span>
              <strong>Cartão travado neste orçamento.</strong>{' '}
              Ao escolher cartão, o cliente vê o aviso de autorização e pode pagar no Pix ou pedir a liberação. A liberação depende de aprovação da gestão (ícone <strong>!</strong> do cabeçalho).
            </span>
          </div>
        )}

        <div className="px-7 pt-6">
          {/* Título + badge status cliente + botões de ação */}
          <div className="flex items-center justify-between mt-2 text-2xl" style={{ color: C.textMain }}>
            <div className="flex items-center gap-4 flex-wrap">
              <span>Orçamento {presales.references || ''}</span>
              <CustomerStatusBadge presales={presales} />
            </div>
            {!approve && <div className="w-100">
              <Button
                textcolor="#fff"
                padding="6px 15px"
                margintop="0px"
                marginright="20px"
                width="100px"
                onClick={() => history.push(`/admin/orcamentos/editar/${presales_id}`)}
                customcolor={props.theme.color}
                disabled={presales?.status != 'canceled' ? false : true}
              >
                <Icon className="mr-2">
                  edit
                </Icon>
                Editar
              </Button>
              {((presales?.status === 'overdue') || (presales?.status === 'pending' && presales?.expires_at && new Date(presales.expires_at) < new Date())) && (presales?.renewal_count ?? 0) < 3 && (
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  margintop="0px"
                  marginright="20px"
                  width="180px"
                  onClick={renovarPresale}
                  customcolor="#0a7c5c"
                >
                  <Icon className="mr-2">refresh</Icon>
                  Renovar ({3 - (presales?.renewal_count ?? 0)} restantes)
                </Button>
              )}
              {presales?.status === 'canceled' && (
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  margintop="0px"
                  marginright="20px"
                  width="200px"
                  onClick={reactivatePresale}
                  customcolor="#0a7c5c"
                >
                  <Icon className="mr-2">restore</Icon>
                  Reativar orçamento
                </Button>
              )}
              {presales?.status !== 'canceled' && presales?.status !== 'sold' && (!presales?.card_hold || isManager) && (
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  margintop="0px"
                  marginright="20px"
                  width="230px"
                  onClick={() => toggleCardHold(!presales?.card_hold)}
                  customcolor={presales?.card_hold ? '#0a7c5c' : '#7c3aed'}
                >
                  <Icon className="mr-2">{presales?.card_hold ? 'lock_open' : 'lock'}</Icon>
                  {presales?.card_hold ? 'Remover trava de cartão' : 'Travar cartão (suspeita)'}
                </Button>
              )}
              <Button
                textcolor="#fff"
                padding="6px 15px"
                margintop="0px"
                marginright="20px"
                width="250px"
                onClick={() => setDisableModal(true)}
                bgColor="#F44336"
                disabled={presales?.status != 'canceled' ? false : true}
              >
                <Icon className="mr-2">delete</Icon>
                Cancelar orçamento
              </Button>
              <DeleteModal
                open={disableModal}
                close={() => setDisableModal(false)}
                delete={() => cancelPresale(`/presales/${presales_id}/cancel`)}
                title="Cancelar orçamento?"
                redirect={'/admin/orcamentos'}
                description="Você realmente deseja cancelar este orçamento? Essa ação é irreversível,
              mas você poderá cadastrá-lo no futuro novamente."
              />
            </div>}
          </div>

          {/* Status do orçamento interno */}
          {!approve && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>Status do orçamento</div>
              <div style={{ padding: 16 }}>
                <div className="flex text-base font-normal items-center" style={{ color: C.textMain }}>
                  <div className="flex">
                    {status[presales?.status_discount_approve]?.text}{' '}
                    <Icon className="ml-2">{status[presales?.status_discount_approve]?.icon}</Icon>
                  </div>
                  {presales?.status_discount_approve === 'sold' && (
                    <Link color="inherit" to={`/admin/reservas/detalhe/${presales.sale_id}`}>
                      <Button
                        textcolor="#fff"
                        padding="2px"
                        width="100px"
                        marginleft="10px"
                        margintop="0px"
                        customcolor={props.theme.color}
                      >
                        Ver venda
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Aprovação Gerencial ── */}
          {(() => {
            const apSt = presales.approval_status;
            const apType = presales.approval_type;
            const apVal  = presales.approval_value;
            const apMgrVal = presales.approval_manager_value;
            const apNote = presales.approval_manager_note;

            const typeLabel = (type, val) => {
              if (type === 'discount_pct')   return `Desconto de ${val}%`;
              if (type === 'discount_value') return `Desconto de R$ ${val}`;
              if (type === 'installment')    return `${val}x sem juros`;
              return val;
            };

            const inputStyle = {
              width: '100%', boxSizing: 'border-box',
              background: '#0f1e35', border: `1px solid ${C.hover}`,
              borderRadius: 8, padding: '9px 12px',
              color: C.textMain, fontSize: 14, outline: 'none',
            };
            const labelStyle = { color: C.textSec, fontSize: 12, display: 'block', marginBottom: 5 };

            // Consultor vê o card apenas quando !approve
            // Gerente vê o card quando há um pedido pendente (em qualquer view)
            const shouldShow = !approve || (isManager && apSt === 'pending');
            if (!shouldShow) return null;

            return (
              <div style={cardStyle}>
                <div style={{ ...cardHeaderStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon style={{ fontSize: 18, color: C.accent }}>admin_panel_settings</Icon>
                  Aprovação Gerencial
                  {apSt === 'pending'  && <span style={{ marginLeft: 8, background: '#78350f', color: '#fde68a', borderRadius: 20, padding: '2px 10px', fontSize: 12 }}>⏳ Aguardando</span>}
                  {apSt === 'approved' && <span style={{ marginLeft: 8, background: '#14532d', color: '#bbf7d0', borderRadius: 20, padding: '2px 10px', fontSize: 12 }}>✓ Aprovado</span>}
                  {apSt === 'refused'  && <span style={{ marginLeft: 8, background: '#7f1d1d', color: '#fecaca', borderRadius: 20, padding: '2px 10px', fontSize: 12 }}>✗ Não aprovado</span>}
                </div>
                <div style={{ padding: 16 }}>

                  {/* ── Sem pedido — todos veem formulário ── */}
                  {!apSt && (
                    <>
                      <p style={{ color: C.textSec, fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
                        Solicite uma condição especial para este cliente. O gerente receberá o pedido e poderá aprovar ou recusar.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={labelStyle}>Tipo de condição</label>
                          <select
                            value={approvalForm.type}
                            onChange={e => setApprovalForm(f => ({ ...f, type: e.target.value }))}
                            style={inputStyle}
                          >
                            <option value="discount_pct">Desconto (%)</option>
                            <option value="discount_value">Desconto (R$)</option>
                            <option value="installment">Parcelas sem juros (nº)</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>
                            {approvalForm.type === 'discount_pct'   && 'Percentual (ex: 10)'}
                            {approvalForm.type === 'discount_value' && 'Valor em R$ (ex: 200)'}
                            {approvalForm.type === 'installment'    && 'Nº de parcelas (ex: 12)'}
                          </label>
                          <input
                            type="text"
                            value={approvalForm.value}
                            onChange={e => setApprovalForm(f => ({ ...f, value: e.target.value }))}
                            placeholder={
                              approvalForm.type === 'discount_pct'   ? '10' :
                              approvalForm.type === 'discount_value' ? '200' : '12'
                            }
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>Justificativa (opcional)</label>
                        <textarea
                          value={approvalForm.justify}
                          onChange={e => setApprovalForm(f => ({ ...f, justify: e.target.value }))}
                          placeholder="Ex: cliente indeciso, pediu condição especial para fechar..."
                          rows={2}
                          style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit' }}
                        />
                      </div>
                      <button
                        onClick={requestApproval}
                        disabled={approvalLoading || !approvalForm.value.trim()}
                        style={{
                          background: approvalLoading || !approvalForm.value.trim() ? C.hover : C.btnDef,
                          color: C.textMain, border: 'none', borderRadius: 8,
                          padding: '9px 20px', cursor: approvalLoading || !approvalForm.value.trim() ? 'not-allowed' : 'pointer',
                          fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        <Icon style={{ fontSize: 16 }}>send</Icon>
                        {approvalLoading ? 'Enviando...' : 'Pedir aprovação gerencial'}
                      </button>
                    </>
                  )}

                  {/* ── Pendente — consultor vê aguardando ── */}
                  {apSt === 'pending' && !isManager && (
                    <div>
                      <div style={{ color: C.textSec, fontSize: 13, marginBottom: 6 }}>Pedido enviado ao gerente:</div>
                      <div style={{ color: C.textMain, fontWeight: 600, fontSize: 15 }}>
                        {typeLabel(apType, apVal)}
                      </div>
                      {presales.approval_justify && (
                        <div style={{ color: C.textSec, fontSize: 13, marginTop: 6 }}>
                          Justificativa: {presales.approval_justify}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Pendente — gerente pode aprovar/recusar ── */}
                  {apSt === 'pending' && isManager && (
                    <>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ color: C.textSec, fontSize: 12, marginBottom: 4 }}>Consultor solicitou:</div>
                        <div style={{ color: C.textMain, fontWeight: 600, fontSize: 15 }}>{typeLabel(apType, apVal)}</div>
                        {presales.approval_justify && (
                          <div style={{ color: C.textSec, fontSize: 13, marginTop: 4 }}>
                            Justificativa: {presales.approval_justify}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={labelStyle}>
                            Valor aprovado{' '}
                            <span style={{ fontSize: 11 }}>(pode alterar)</span>
                          </label>
                          <input
                            type="text"
                            value={managerForm.value || apVal}
                            onChange={e => setManagerForm(f => ({ ...f, value: e.target.value }))}
                            placeholder={apVal}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Nota interna (opcional)</label>
                          <input
                            type="text"
                            value={managerForm.note}
                            onChange={e => setManagerForm(f => ({ ...f, note: e.target.value }))}
                            placeholder="Observação para o consultor..."
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={refuseManagerApproval}
                          disabled={managerLoading}
                          style={{
                            background: 'transparent', border: '1px solid #ef4444',
                            color: '#fca5a5', borderRadius: 8,
                            padding: '8px 20px', cursor: managerLoading ? 'not-allowed' : 'pointer',
                            fontWeight: 600, fontSize: 14,
                          }}
                        >
                          {managerLoading ? '...' : 'Não aprovar'}
                        </button>
                        <button
                          onClick={approveManagerApproval}
                          disabled={managerLoading || !(managerForm.value || apVal)}
                          style={{
                            background: managerLoading ? C.hover : '#14532d',
                            color: '#bbf7d0', border: '1px solid #22c55e', borderRadius: 8,
                            padding: '8px 20px', cursor: managerLoading ? 'not-allowed' : 'pointer',
                            fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          <Icon style={{ fontSize: 15 }}>check_circle</Icon>
                          {managerLoading ? 'Salvando...' : 'Aprovar'}
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── Aprovado ── */}
                  {apSt === 'approved' && (
                    <div>
                      <div style={{ color: '#bbf7d0', fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                        🎉 {typeLabel(apType, apMgrVal || apVal)}
                      </div>
                      {apMgrVal && apMgrVal !== apVal && (
                        <div style={{ color: C.textSec, fontSize: 13 }}>
                          (solicitado: {typeLabel(apType, apVal)} → aprovado: {typeLabel(apType, apMgrVal)})
                        </div>
                      )}
                      {apNote && (
                        <div style={{ color: C.textSec, fontSize: 13, marginTop: 4 }}>
                          Nota: {apNote}
                        </div>
                      )}
                      {isManager && (
                        <button
                          onClick={removeManagerApproval}
                          disabled={managerLoading}
                          style={{
                            marginTop: 12, background: 'transparent', border: '1px solid #ef4444',
                            color: '#fca5a5', borderRadius: 8, padding: '8px 16px', fontSize: 13,
                            cursor: managerLoading ? 'not-allowed' : 'pointer', opacity: managerLoading ? 0.6 : 1,
                          }}
                        >
                          Remover desconto gerencial
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Recusado ── */}
                  {apSt === 'refused' && (
                    <div>
                      <div style={{ color: '#fca5a5', fontSize: 14, marginBottom: 4 }}>
                        Pedido de {typeLabel(apType, apVal)} não aprovado.
                      </div>
                      {apNote && (
                        <div style={{ color: C.textSec, fontSize: 13 }}>
                          Motivo: {apNote}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            );
          })()}

          {/* Informações do cliente + pedido */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>Informações do cliente</div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, color: C.textMain, fontSize: 15 }}>
                <div>{presales.name}</div>
                <div>{presales.email}</div>
                <div>Documento: {presales.document}</div>
              </div>
            </div>

            <div style={{ padding: '16px', fontSize: 18, color: C.textMain, borderTop: `1px solid ${C.hover}` }}>
              Informações do pedido
            </div>
            {budgets && (
              <div style={{ padding: 16 }}>
                <RequestTable {...props} dataBudgets={budgets} show={true} total={presales.total} urlLink={urlLink}
                  presaleApproval={presales.approval_status === 'approved' ? {
                    type:         presales.approval_type,
                    managerValue: presales.approval_manager_value,
                    justify:      presales.approval_justify,
                    managerNote:  presales.approval_manager_note,
                  } : null}
                />
              </div>
            )}
          </div>

          {/* Vendedor */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>Informações do vendedor</div>
            <div style={{ display: 'flex', padding: 16 }}>
              <div style={{ flex: 1, color: C.textSec }}>Vendedor responsável</div>
              <div style={{ flex: 1, color: C.textMain }}>
                {presales.seller_name} ({presales.seller_email})
              </div>
            </div>
          </div>

          {/* Thread de comentários */}
          <CommentsThread presalesId={presales_id} />

          {/* Rodapé — botões de ação */}
          <div className="flex justify-end mb-5 space-x-5">
            {approve && presales?.approval_status !== 'pending' ?
              <>
                <Button
                  textcolor="#e8f0fb"
                  padding="6px 15px"
                  width="200"
                  variant="outlined"
                  bgColor="transparent"
                  onClick={disaprovePresale}
                  disabled={presales?.status != 'canceled' ? false : true}
                >
                  Recusar desconto
                </Button>
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  width="200"
                  variant="outlined"
                  customcolor={props.theme.color}
                  onClick={approvePresale}
                  disabled={presales?.status != 'canceled' ? false : true}
                >
                  Aprovar desconto
                </Button>
              </>
              :
              <>
                <a href={`/voucher-presale/${presales_id}/${urlLink}`} target='_blank' rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <Button
                    textcolor="#fff"
                    padding="6px 15px"
                    width="200px"
                    customcolor={props.theme.color}
                    disabled={presales?.status != 'canceled' ? false : true}
                  >
                    Ver documento
                  </Button>
                </a>
                {presales?.sale_id && (
                  <a href={`/vs/${presales.sale_id}`} target="_blank" rel="noopener noreferrer">
                    <Button
                      textcolor="#fff"
                      padding="6px 15px"
                      width="200px"
                      customcolor={props.theme.color}
                    >
                      <Icon style={{ fontSize: 16, marginRight: 6 }}>receipt_long</Icon>
                      Ver voucher
                    </Button>
                  </a>
                )}
                <Button
                  textcolor="#e8f0fb"
                  padding="6px 15px"
                  width="200px"
                  variant="outlined"
                  bgColor="transparent"
                  onClick={openSendEmailModal}
                  disabled={presales?.status === 'canceled'}
                >
                  <Icon style={{ fontSize: 16, marginRight: 6 }}>email</Icon>
                  Reenviar email
                </Button>
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  width="200px"
                  disabled={presales?.status === 'canceled' || presales?.approval_status === 'pending'}
                  onClick={() => saleConvert(presales_id)}
                  customcolor={props.theme.color}
                >
                  Converter em compra
                </Button>
                {userPermission === 'Administrador'
                  && !['paid', 'partial', 'accomplished'].includes(presales?.payment_status)
                  && presales?.status !== 'canceled' && (
                  <Button
                    textcolor="#e8f0fb"
                    padding="6px 15px"
                    width="240px"
                    variant="outlined"
                    bgColor="transparent"
                    onClick={() => setExtPayModal(true)}
                  >
                    <Icon style={{ fontSize: 16, marginRight: 6 }}>receipt_long</Icon>
                    Registrar pagamento externo
                  </Button>
                )}
              </>
            }
            {/* <Button
              textcolor="#e8f0fb"
              padding="6px 15px"
              width="200"
              variant="outlined"
              onClick={() => setSendVoucherModal(true)}
              bgColor="transparent"
            >
              <Icon className="mr-2">
                email
              </Icon>
              Reenviar voucher
            </Button> */}
            <SendVoucherModal
              saleId={presales_id}
              open={sendVoucherModal}
              close={() => setSendVoucherModal(false)}
              {...props}
            />
          </div>
        </div>
      </>
    }

    {/* ── Modal: reenviar email do orçamento ── */}
    {sendEmailModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#172B4D', borderRadius: 16, padding: '32px 28px',
          width: 420, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Icon style={{ color: '#62DEC2', fontSize: 22 }}>email</Icon>
            <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 16 }}>
              Reenviar email do orçamento
            </span>
          </div>

          {sendEmailSuccess ? (
            <div style={{
              background: '#14532d', border: '1px solid #22c55e', borderRadius: 10,
              padding: '14px 16px', color: '#bbf7d0', textAlign: 'center', fontSize: 14,
            }}>
              ✓ Email enviado com sucesso!
            </div>
          ) : (
            <>
              <label style={{ color: '#8ba5c4', fontSize: 13, display: 'block', marginBottom: 8 }}>
                Endereço de e-mail do destinatário
              </label>
              <input
                type="email"
                value={sendEmailAddress}
                onChange={e => setSendEmailAddress(e.target.value)}
                placeholder="email@cliente.com"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#0f1e35', border: '1px solid #2a4f72',
                  borderRadius: 8, padding: '10px 14px',
                  color: '#e8f0fb', fontSize: 14, outline: 'none',
                  marginBottom: 20,
                }}
              />
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSendEmailModal(false)}
                  style={{
                    background: 'transparent', border: '1px solid #2a4f72',
                    borderRadius: 8, padding: '8px 20px',
                    color: '#8ba5c4', cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={!sendEmailAddress || sendEmailLoading}
                  style={{
                    background: '#62DEC2', border: 'none', borderRadius: 8,
                    padding: '8px 24px', color: '#0f1e35',
                    fontWeight: 700, cursor: sendEmailLoading ? 'not-allowed' : 'pointer',
                    fontSize: 14, opacity: (!sendEmailAddress || sendEmailLoading) ? 0.6 : 1,
                  }}
                >
                  {sendEmailLoading ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {/* ── Modal: registrar pagamento externo (só admin) ── */}
    {extPayModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#172B4D', borderRadius: 16, padding: '28px 26px',
          width: 460, maxWidth: '92vw', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Icon style={{ color: '#62DEC2', fontSize: 22 }}>receipt_long</Icon>
            <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 16 }}>
              Registrar pagamento externo
            </span>
          </div>
          <p style={{ color: '#8ba5c4', fontSize: 12, lineHeight: 1.5, marginBottom: 18 }}>
            Pagamento feito por fora do Asaas. O <b>valor é calculado pelo sistema</b> (com desconto
            gerencial e do método). Ao confirmar, o orçamento é marcado como pago e o cliente recebe a
            mensagem no chat, igual à integração.
          </p>

          {/* Método */}
          <label style={{ color: '#8ba5c4', fontSize: 12, display: 'block', marginBottom: 6 }}>Método</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[{ v: 'pix', t: 'PIX' }, { v: 'credit_card', t: 'Cartão' }].map(m => (
              <button key={m.v} onClick={() => setExtPayForm(f => ({ ...f, method: m.v }))}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  background: extPayForm.method === m.v ? '#62DEC2' : 'transparent',
                  color: extPayForm.method === m.v ? '#0f2340' : '#8ba5c4',
                  border: `1px solid ${extPayForm.method === m.v ? '#62DEC2' : '#2a4f72'}`,
                }}>{m.t}</button>
            ))}
          </div>

          {/* Tipo (parcial) */}
          <label style={{ color: '#8ba5c4', fontSize: 12, display: 'block', marginBottom: 6 }}>Pagamento</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[{ v: 100, t: 'Total' }, { v: 50, t: 'Entrada 50%' }, { v: 30, t: 'Entrada 30%' }].map(p => (
              <button key={p.v} onClick={() => setExtPayForm(f => ({ ...f, partial_pct: p.v }))}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                  background: extPayForm.partial_pct === p.v ? '#62DEC2' : 'transparent',
                  color: extPayForm.partial_pct === p.v ? '#0f2340' : '#8ba5c4',
                  border: `1px solid ${extPayForm.partial_pct === p.v ? '#62DEC2' : '#2a4f72'}`,
                }}>{p.t}</button>
            ))}
          </div>

          {/* Comprovante */}
          <label style={{ color: '#8ba5c4', fontSize: 12, display: 'block', marginBottom: 6 }}>Comprovante (obrigatório)</label>
          <input type="file" accept="image/*,application/pdf"
            onChange={e => setExtPayFile(e.target.files?.[0] || null)}
            style={{ width: '100%', color: '#e8f0fb', fontSize: 13, marginBottom: 6 }} />
          {extPayFile && <div style={{ color: '#62DEC2', fontSize: 12, marginBottom: 8 }}>✓ {extPayFile.name}</div>}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 18 }}>
            <button onClick={() => { setExtPayModal(false); setExtPayFile(null); }}
              style={{ background: 'transparent', border: '1px solid #2a4f72', borderRadius: 8, padding: '8px 20px', color: '#8ba5c4', cursor: 'pointer', fontSize: 14 }}>
              Cancelar
            </button>
            <button onClick={submitExternalPayment} disabled={!extPayFile || extPayLoading}
              style={{
                background: '#62DEC2', border: 'none', borderRadius: 8, padding: '8px 24px',
                color: '#0f2340', fontWeight: 700, fontSize: 14,
                cursor: (!extPayFile || extPayLoading) ? 'not-allowed' : 'pointer',
                opacity: (!extPayFile || extPayLoading) ? 0.6 : 1,
              }}>
              {extPayLoading ? 'Registrando...' : 'Confirmar pagamento'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Modal: dados de transfer antes de converter ── */}
    {transferInfoModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}>
        <div style={{
          background: '#172B4D',
          borderRadius: 14,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 540,
          maxHeight: '85vh',
          overflowY: 'auto',
          color: '#e8f0fb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Icon style={{ color: '#62DEC2', fontSize: 22 }}>flight</Icon>
            <span style={{ fontSize: 17, fontWeight: 700 }}>Confirmar dados dos transfers</span>
          </div>
          <p style={{ color: '#8ba5c4', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
            Preencha a data, número do voo e horário de cada transfer antes de converter.
          </p>

          {transferIssues.map(b => {
            const fix = transferFix[b.id] || {};
            const label = b.travel_name || (b.transfer_type === 'in' ? 'Transfer IN' : 'Transfer OUT');
            const typeLabel = b.transfer_type === 'in' ? '✈️ IN' : '✈️ OUT';
            const inputStyle = {
              width: '100%', background: '#0f1c2e', color: '#e8f0fb',
              border: '1px solid #2a4f72', borderRadius: 8,
              padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
              outline: 'none',
            };
            return (
              <div key={b.id} style={{
                background: '#0f1c2e', borderRadius: 10,
                padding: '14px 16px', marginBottom: 14,
                border: '1px solid #2a4f72',
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                  <span style={{ color: '#62DEC2', marginRight: 8 }}>{typeLabel}</span>
                  {label}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ color: '#8ba5c4', fontSize: 11, display: 'block', marginBottom: 4 }}>
                      Data do transfer *
                    </label>
                    <input
                      type="date"
                      value={fix.date || ''}
                      onChange={e => setTransferFix(prev => ({ ...prev, [b.id]: { ...prev[b.id], date: e.target.value } }))}
                      style={{ ...inputStyle, colorScheme: 'dark' }}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#8ba5c4', fontSize: 11, display: 'block', marginBottom: 4 }}>
                      Número do voo
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: LA706"
                      value={fix.flight_code || ''}
                      onChange={e => setTransferFix(prev => ({ ...prev, [b.id]: { ...prev[b.id], flight_code: e.target.value } }))}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#8ba5c4', fontSize: 11, display: 'block', marginBottom: 4 }}>
                      Horário do voo
                    </label>
                    <TimePicker
                      label=""
                      value={fix.flight_time ? new Date(`1970-01-01T${fix.flight_time}`) : null}
                      onChange={time => {
                        const hhmm = time
                          ? `${String(time.getHours()).padStart(2,'0')}:${String(time.getMinutes()).padStart(2,'0')}`
                          : '';
                        setTransferFix(prev => ({ ...prev, [b.id]: { ...prev[b.id], flight_time: hhmm } }));
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={() => { setTransferInfoModal(false); setConverting(false); }}
              style={{
                flex: 1, background: 'transparent', border: '1px solid #2a4f72',
                color: '#8ba5c4', borderRadius: 8, padding: '10px 0',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Cancelar
            </button>
            <button
              onClick={saveTransferInfoAndContinue}
              disabled={converting || transferIssues.some(b => !transferFix[b.id]?.date)}
              style={{
                flex: 2,
                background: converting ? '#2a4f72' : (props.theme?.color || '#62DEC2'),
                color: '#fff',
                border: 'none', borderRadius: 8,
                padding: '10px 0',
                cursor: converting ? 'default' : 'pointer',
                fontSize: 13, fontWeight: 700,
                opacity: transferIssues.some(b => !transferFix[b.id]?.date) ? 0.5 : 1,
              }}
            >
              {converting ? 'Salvando...' : 'Confirmar e continuar'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Modal: datas com problema antes de converter ── */}
    {dateCheckModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}>
        <div style={{
          background: '#172B4D',
          borderRadius: 14,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 520,
          maxHeight: '85vh',
          overflowY: 'auto',
          color: '#e8f0fb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Icon style={{ color: '#fb923c', fontSize: 22 }}>warning</Icon>
            <span style={{ fontSize: 17, fontWeight: 700 }}>Confirmar datas antes de converter</span>
          </div>
          <p style={{ color: '#8ba5c4', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
            Os passeios abaixo não têm data definida ou a data escolhida não tem vagas suficientes.
            Selecione uma nova data para cada um antes de continuar.
          </p>

          {dateIssues.map(issue => {
            const availList = fixAvailDates[issue.budget_id] || [];
            const currentDate = fixDates[issue.budget_id] || '';
            const isValid = currentDate && availList.some(
              d => d.cupo_date === currentDate && (d.availables_cupos ?? d.total_cupos) >= issue.cupos_needed
            );
            return (
              <div key={issue.budget_id} style={{
                background: '#0f1c2e',
                borderRadius: 10,
                padding: '14px 16px',
                marginBottom: 14,
                border: `1px solid ${isValid ? '#14432a' : '#3d2a0a'}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  {issue.tour_name}
                </div>
                <div style={{ fontSize: 12, color: '#8ba5c4', marginBottom: 10 }}>
                  {issue.issue === 'no_date'
                    ? '⚠️ Sem data definida'
                    : `⚠️ Data ${issue.tour_date ? new Date(`${issue.tour_date}T00:00:00`).toLocaleDateString('pt-BR') : '—'} — ${issue.cupos_available} vaga${issue.cupos_available !== 1 ? 's' : ''} disponível (necessário: ${issue.cupos_needed})`
                  }
                </div>
                <select
                  value={currentDate}
                  onChange={e => setFixDates(prev => ({ ...prev, [issue.budget_id]: e.target.value }))}
                  style={{
                    width: '100%',
                    background: '#172B4D',
                    color: '#e8f0fb',
                    border: `1px solid ${isValid ? '#62DEC2' : '#2a4f72'}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 13,
                  }}
                >
                  <option value="">— Selecione uma data —</option>
                  {/* Datas com vagas suficientes */}
                  {availList
                    .filter(d => (d.availables_cupos ?? d.total_cupos) >= issue.cupos_needed)
                    .sort((a, b) => a.cupo_date.localeCompare(b.cupo_date))
                    .map(d => (
                      <option key={d.cupo_date} value={d.cupo_date}>
                        {new Date(`${d.cupo_date}T00:00:00`).toLocaleDateString('pt-BR')} — {d.availables_cupos ?? d.total_cupos} vagas
                      </option>
                    ))
                  }
                  {/* Data sugerida sem vagas suficientes — aparece desabilitada para referência */}
                  {issue.tour_date && !availList.some(d => d.cupo_date === issue.tour_date && (d.availables_cupos ?? d.total_cupos) >= issue.cupos_needed) && (
                    <option value={issue.tour_date} disabled style={{ color: '#8ba5c4' }}>
                      {new Date(`${issue.tour_date}T00:00:00`).toLocaleDateString('pt-BR')} — data sugerida (sem vagas)
                    </option>
                  )}
                </select>
                {isValid && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#62DEC2' }}>✓ Data válida selecionada</div>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={() => { setDateCheckModal(false); setConverting(false); }}
              style={{
                flex: 1, background: 'transparent', border: '1px solid #2a4f72',
                color: '#8ba5c4', borderRadius: 8, padding: '10px 0',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Cancelar
            </button>
            <button
              onClick={saveFixedDatesAndConvert}
              disabled={converting || dateIssues.some(i => {
                const d = fixDates[i.budget_id];
                const avail = fixAvailDates[i.budget_id] || [];
                return !d || !avail.some(a => a.cupo_date === d && (a.availables_cupos ?? a.total_cupos) >= i.cupos_needed);
              })}
              style={{
                flex: 2,
                background: converting ? '#2a4f72' : props.theme?.color || '#62DEC2',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 0',
                cursor: converting ? 'default' : 'pointer',
                fontSize: 13,
                fontWeight: 700,
                opacity: dateIssues.some(i => {
                  const d = fixDates[i.budget_id];
                  const avail = fixAvailDates[i.budget_id] || [];
                  return !d || !avail.some(a => a.cupo_date === d && a.total_cupos >= i.cupos_needed);
                }) ? 0.5 : 1,
              }}
            >
              {converting ? 'Convertendo...' : 'Salvar datas e converter'}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
}

const mapStateToProps = (state) => ({ user: state.user });
export default connect(mapStateToProps)(Show);
